import { useEffect, useRef, useState } from 'react'
import { type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import LogTool from '../../../logger/logTools'
import { createContact, getContactRepresentation } from '../../../utils/functions'
import {
  ContactGroup,
  type Contact,
  type RichContact,
  type SelectOption,
} from '../../../utils/types'
import { useAllRichContacts } from '../../Contact/hooks/useContactQueries'
import { buildRichContactGrouping } from '../../Contact/utils/richContactHelpers'
import { useCreateContact } from '../../Contact/hooks/useContactMutations'
import { useCreateContactGroup } from '../../Contact/hooks/useContactGroupMutations'
import { useAllContactGroups } from '../../Contact/hooks/useContactGroupQueries'
import { createContactGroup } from '../../Contact/api/contactApi'

const log = new LogTool({ context: 'useContactHandler', enable: true, logLevel: 'warn' })

type AutomaticGroupShareFormInput = {
  [inputKey: string]: { group: SelectOption; customInvitationMessage: string }
}

/**
 * Custom hook that handles all contact-related logic for Sharepoint Drawers.
 * This hook manages:
 * - Loading contacts from the API
 * - Storing contacts in a ref
 * - Managing contact options for select inputs
 * - Managing contact form input state for ContactDrawer
 * - Creating new contacts and updating all related state
 *
 * @param isGuestUser - Whether the current user is a guest user (contacts are disabled for guests)
 * @param setAutomaticGroupSharesFormInput - State setter for automatic shares form input (used to update when new contact is created)
 * @returns Object containing contactStore, contactOptions, contactFormInput state, newContactReceiver state, and handleAddContactOption function
 */
export function useContactGroupHandler(
  isGuestUser: boolean,
  setAutomaticGroupSharesFormInput: Dispatch<SetStateAction<AutomaticGroupShareFormInput>>
): {
  contactGroupStore: MutableRefObject<ContactGroup[]>
  contactGroupOptions: SelectOption[]
  contactGroupFormInput: any
  setContactGroupFormInput: Dispatch<SetStateAction<any>>
  newGroupReceiver: string | undefined
  setNewGroupReceiver: Dispatch<SetStateAction<string | undefined>>
  handleAddContactGroupOption: (contactGroupInput: any) => Promise<'SUCCESS' | 'ERROR'>
} {
  const contactGroupStore = useRef<ContactGroup[]>([])
  const [contactGroupOptions, setContactGroupOptions] = useState<SelectOption[]>([])
  const [contactGroupFormInput, setContactGroupFormInput] = useState<any>({})
  const [newContactGroupReceiver, setNewContactGroupReceiver] = useState<string | undefined>(
    undefined
  )
  const createContactGroupMutation = useCreateContactGroup()
  const { t } = useTranslation()

  // Fetch rich contacts using React Query (disabled for guest users)
  const {
    data: contactGroupsData,
    isError: contactGroupsError,
    error: contactGroupsErrorObj,
  } = useAllContactGroups({}, !isGuestUser)

  // Initialize contacts from API data
  useEffect(() => {
    if (contactGroupsError && contactGroupsErrorObj) {
      log.error('Error fetching rich contacts', contactGroupsErrorObj)
    }
  }, [contactGroupsError, contactGroupsErrorObj])

  useEffect(() => {
    if (!contactGroupsData) return
    contactGroupStore.current = [...contactGroupsData]
    setContactGroupOptions(
      contactGroupStore.current.map(group => ({
        value: group.self,
        label: group.name,
      }))
    )
  }, [contactGroupsData])

  const handleAddContactGroupOption = async (
    contactGroupInput: any
  ): Promise<'SUCCESS' | 'ERROR'> => {
    log.info('Begin sending contact group.')
    log.debug('Input ->', contactGroupInput)

    try {
      // create new contact group
      const newContactGroup = await createContactGroupMutation.mutateAsync(contactGroupInput)
      contactGroupStore.current.push(newContactGroup)
      setContactGroupOptions(prev => [
        ...prev,
        {
          value: newContactGroup.self,
          label: newContactGroup.name,
        },
      ])

      // update the select that was used to initiate the contact creation process
      if (newContactGroupReceiver) {
        setAutomaticGroupSharesFormInput(prev => {
          const updated = { ...prev }
          updated[newContactGroupReceiver] = {
            group: {
              value: newContactGroup.self,
              label: newContactGroup.name,
            },
            customInvitationMessage: '',
          }
          return updated
        })
      }

      return 'SUCCESS'
    } catch (error) {
      log.error('Error creating contact option:', error)
      return 'ERROR'
    }
  }

  return {
    contactGroupStore: contactGroupStore,
    contactGroupOptions: contactGroupOptions,
    contactGroupFormInput: contactGroupFormInput,
    setContactGroupFormInput: setContactGroupFormInput,
    newGroupReceiver: newContactGroupReceiver,
    setNewGroupReceiver: setNewContactGroupReceiver,
    handleAddContactGroupOption: handleAddContactGroupOption,
  }
}
